import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/client/lib";
import { getToken, setToken, clearToken } from "@/client/lib/auth";

type AuthContextValue = {
    isAuthenticated: boolean;
    token: string | null;
    login: (username: string, password: string) => Promise<{ error?: string }>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const [token, setTokenState] = useState<string | null>(getToken);

    const login = useCallback(
        async (username: string, password: string) => {
            const basic = btoa(`${username.trim()}:${password}`);
            const { data, error } = await api.users.login.post(undefined, {
                headers: { authorization: `Basic ${basic}` },
            });

            if (error) {
                const message =
                    error.value && typeof error.value === "object" && "message" in error.value
                        ? String(error.value.message)
                        : `Login failed (${error.status})`;
                return { error: message };
            }

            if (!data?.token) {
                return { error: "Login failed: no token received" };
            }

            setToken(data.token);
            setTokenState(data.token);
            return {};
        },
        []
    );

    const logout = useCallback(async () => {
        await api.users.logout.post(undefined, {}).catch(() => {});
        clearToken();
        setTokenState(null);
        navigate("/login", { replace: true });
    }, [navigate]);

    const value = useMemo<AuthContextValue>(
        () => ({
            isAuthenticated: token !== null,
            token,
            login,
            logout,
        }),
        [token, login, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
